import { useEffect, useMemo, useState } from 'react'
import { icons, logos } from 'common'
import { CopyIcon, PublicPresaleCSS } from './PublicPresaleCSS.styled'
import { ContentSwitcherByState } from 'components/Atoms/ContentSwitcherByState'
import { Helmet } from 'react-helmet'
import { currencyFormatter, textMiddleTruncate } from 'utils'

import { utils } from 'ethers'
import {
  useEthers,
  useEtherBalance,
  useContractFunction,
  Mainnet,
  Sepolia,
  useCall,
} from '@usedapp/core'
import { formatEther, formatUnits } from '@ethersproject/units'
import { Zero } from '@ethersproject/constants'
import {
  tokenSaleContract,
  tokenSaleAddress,
} from 'constants/contracts/tokenSale'
import { SingleDataLoader } from 'components/Atoms/SingleDataLoader'
import { LONG_DASH } from 'constants'
import { doCopy } from 'utils'
import { Link } from 'components/Atoms/Link'
import { useNavigate, useParams } from 'react-router-dom'
import { useAPI } from 'utils/hooks/useAPI'
import { API } from 'constants/apiEndPoints'
import { onlyDigits } from 'utils/uiUtils_TS'
import twitterShare from 'twitter-share'

const NETWORK = Mainnet //Sepolia //Mainnet
const YOLO_TOKEN_USD = 0.04
const MIN_USD_CONTRIBUTION = 1000
// const YOLO_TOKEN_DECIMALS = 18

const VALID_INFLUENCERS = ['wiz', 'defi_brian', 'aditya', 'mike', 'hoss']

const useEthPrice = () => {
  const CONTRACT_ETH_DECIMALS = 8
  const { value: ethPriceArrayBN = [Zero] } =
    useCall({
      contract: tokenSaleContract,
      method: 'getLatestEthPrice',
    }) ?? {}

  const ethPrice = useMemo(() => {
    const ethPriceBN = ethPriceArrayBN?.at(0)
    const ethPrice = formatUnits(ethPriceBN, CONTRACT_ETH_DECIMALS)
    return Number(ethPrice)
  }, [ethPriceArrayBN])

  return ethPrice
}

const usePurchasedAmount = () => {
  const web3 = useEthers()
  const { value: purchasedAmount } =
    useCall({
      contract: tokenSaleContract,
      method: 'purchasedAmount',
      args: [web3.account],
    }) ?? {}
  return purchasedAmount
}

//You have insufficient funds to complete your transaction. Please try again.

// There was an unknown error with your transaction. Please try again.

// Your transaction did not meet the $1,000.00 USD minimum. Please try again.

const errorForHumans = (rawError) => {
  let error = rawError
  switch (true) {
    case rawError.includes('insufficient funds'):
      error =
        'You have insufficient funds to complete your transaction. Please try again.'
      break

    default:
      error =
        'There was an unknown error with your transaction. Please try again.'
      break
  }
  return error
}

export const PublicPresale = () => {
  const { influencer } = useParams()
  const navigateTo = useNavigate()
  const [registerTx, setRegisterTx] = useState(false)

  const [stateTxInfo, sendTxInfo, hasTxInfoStatus] = useAPI(
    API.YOLO_TOKEN_PUBLIC_PRESALE,
    {
      controlled: true,
      withJwt: true,
    }
  )

  useEffect(() => {
    const isValidInfluencer = VALID_INFLUENCERS.includes(influencer)
    setRegisterTx(isValidInfluencer)
    if (influencer && !isValidInfluencer) navigateTo('/game/public_presale')
  }, [influencer, navigateTo])

  const openDocsInNewTab = () => {
    const url = 'https://yolorekt.finance/game/yolo_token'
    const newWindow = window.open(url, '_blank')
    newWindow.focus()
  }

  //Web3 code section
  const web3 = useEthers()
  const etherPrice = useEthPrice()
  const purchasedAmount = usePurchasedAmount()
  const etherBalance = useEtherBalance(web3.account)

  const {
    state: tokenPurchaseState,
    send: buyToken,
    resetState: tokenPurchaseResetStatus,
  } = useContractFunction(tokenSaleContract, 'buy', {
    gasLimitBufferPercentage: 10,
    transactionName: 'buyToken',
    chainId: web3.chainId,
  })

  const [connectionState, setConnectionState] = useState('notConnected')
  const [isMainnet, setIsMainnet] = useState(true)

  useEffect(() => {
    if (web3?.account) setConnectionState('connected')
    else setConnectionState('notConnected')
    if (web3?.chainId === NETWORK.chainId) setIsMainnet(true)
    else setIsMainnet(false)
  }, [web3?.account, web3?.chainId])

  const ethBalanceInFiat = useMemo(() => {
    const ethBalance = formatEther(etherBalance || 0)
    return ethBalance * etherPrice
  }, [etherPrice, etherBalance])

  const yoloTokenPurchasedBalance = useMemo(() => {
    const yoloTokenAmount = formatEther(purchasedAmount?.at(0) || Zero)
    return yoloTokenAmount
  }, [purchasedAmount])

  const onMmConnect = (e) => {
    e.preventDefault()
    web3.activateBrowserWallet()
  }

  const onMmDisconnect = (e) => {
    e.preventDefault()
    web3.deactivate()
  }
  const onSwitchToNetwork = (e) => {
    e.preventDefault()
    web3.switchNetwork(NETWORK.chainId)
  }

  // Web3 code section

  // calculator section
  const [ethFiatAmount, setEthFiatAmount] = useState()
  const [ethAmount, setEthAmount] = useState('')
  const [usdAmount, setUsdAmount] = useState('')
  const [yoloAmount, setYoloAmount] = useState('')
  const [error, setError] = useState()
  const [loading, setLoading] = useState(false)

  const coontributeButtonDisabled = useMemo(
    () => !(web3?.account && ethAmount && usdAmount > MIN_USD_CONTRIBUTION),
    [web3?.account, ethAmount, usdAmount]
  )

  const onEthChange = (e) => {
    setError('')
    const inputValue = e.target.value

    const ethAmount = onlyDigits(inputValue)
    const usdAmount = Number((ethAmount * etherPrice).toFixed(2))
    if (inputValue && usdAmount < MIN_USD_CONTRIBUTION) {
      setError(
        `Your contribution does not meet the ${currencyFormatter(
          MIN_USD_CONTRIBUTION,
          {
            decimalDigits: 0,
          }
        )} USD minimum`
      )
    }
    setYoloAmount(usdAmount / YOLO_TOKEN_USD || '')
    setUsdAmount(ethAmount ? usdAmount : '')
    setEthAmount(ethAmount || '')
  }

  const onUsdChange = (e) => {
    setError('')
    const inputValue = e.target.value

    const usdAmount = onlyDigits(inputValue)
    const ethAmount = Number((usdAmount / etherPrice).toFixed(2))
    if (inputValue && usdAmount < MIN_USD_CONTRIBUTION) {
      setError(
        `Your contribution does not meet the ${currencyFormatter(
          MIN_USD_CONTRIBUTION,
          {
            decimalDigits: 0,
          }
        )} USD minimum`
      )
    }
    setYoloAmount(usdAmount / YOLO_TOKEN_USD || '')
    setUsdAmount(usdAmount)
    setEthAmount(ethAmount || '')
  }

  const buyTokens = (e) => {
    e.preventDefault()
    setError('')
    buyToken({ value: utils.parseEther(ethAmount.toString()) })
  }

  const onContributeAgain = (e) => {
    e.preventDefault()
    tokenPurchaseResetStatus()
  }

  useEffect(() => {
    switch (tokenPurchaseState?.status) {
      case 'None':
        break
      case 'PendingSignature':
      case 'Mining':
        setLoading(true)
        break
      case 'Success':
        if (registerTx) {
          const payload = {
            referralName: influencer,
            userAddress: tokenPurchaseState?.receipt?.from,
            transactionHash: tokenPurchaseState?.receipt?.transactionHash,
            status: 'success',
          }
          sendTxInfo({ params: payload })
        }
        setLoading(false)
        break
      case 'Fail':
      case 'Exception':
        if (tokenPurchaseState.errorMessage === 'execution reverted') {
          setError(
            `Your contribution does not meet the ${currencyFormatter(
              MIN_USD_CONTRIBUTION,
              {
                decimalDigits: 0,
              }
            )} USD minimum`
          )
        } else setError(errorForHumans(tokenPurchaseState.errorMessage))
        setLoading(false)
        break
      case 'CollectingSignaturePool':
        break

      default:
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenPurchaseState, influencer, registerTx])
  // calculator section

  //copy to clipboard
  const [hasCopied, setHasCopied] = useState(false)
  useEffect(() => {
    if (hasCopied) {
      const timer = setTimeout(() => {
        setHasCopied(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [hasCopied])

  const onCopy = async (e, txHash) => {
    e.preventDefault()
    e.stopPropagation()
    const copied = await doCopy(txHash)

    if (copied) setHasCopied(true)
  }
  //copy to clipboard

  // Twitter share
  const shareTwitter = (e) => {
    e.preventDefault()
    const params = {
      url: 'https://yolorekt.finance/presale',
      text: "I just contributed to @playyolorekt\\'s official $YOLO Presale. Don\\'t miss out! Get in early to receive airdrops, rewards, and more. Contribute here:",
    }
    const tweetLink = twitterShare(params)
    const a = document.createElement('a')
    a.href = `javascript:window.open('${tweetLink}','_blank','height=400,width=600');`
    a.click()
    a.remove()
  }
  // Twitter share

  return (
    <PublicPresaleCSS>
      <Helmet>
        <title>$YOLO Public Presale</title>
      </Helmet>
      <div className='scroll_wrapper'>
        <div className='yolo_token_page_wrapper'>
          <div className='yt_page_left'>
            <h1>$YOLO Public Presale</h1>

            <form className='calculator'>
              <div className='done'>
                <b>Thank you for your interest in $YOLO</b>
                We'll let you know sometime in the near future about the next
                issuance of the token.
              </div>
            </form>
          </div>

          <div className='yt_page_right'>
            <div className='yt_logo_full'>
              <img alt='' src={logos.yolo_token_full_logo_2} />
            </div>

            <div className='qanda'>
              <div className='question'>About YOLOREKT</div>
              <div className='answer'>
                <div className='callout_boxes'>
                  {/* <div className='box'>
                    <div className='box_title'>Total Volume</div>
                    <div className='box_value'>+600M</div>
                  </div>
                  <div className='box'>
                    <div className='box_title'>Fees generated</div>
                    <div className='box_value'>+150K</div>
                  </div> */}
                </div>
                <p>
                  Launched in 2019 as 3-minute prediction platform on ETH,
                  YOLOREKT has evolved into the ultimate super-degen leveraged
                  gaming platform. YOLOREKT designs, builds, and provides
                  advanced unique leveraged gaming experiences that offer killer
                  1000x leverage using crypto, synthetic stocks, ETFs, and more.
                </p>

                <p>
                  YOLOREKT has a{' '}
                  <b>daily average of over 10 Million in volume</b> and{' '}
                  <b>more than 500 Million total volume</b> since its launch, in
                  a matter of months. YOLOREKT’s growth has been consistent and
                  exponential, inching closer to a profitable platform, and
                  month by month already being cash flow positive.
                </p>
                <p>
                  YOLOREKT boasts of gamified innovation on financial products
                  like never before; it's been called the home for brave degens.
                </p>
              </div>
            </div>

            <div className='landing_level investors'>
              <div className='section_heading'>Seed Investors</div>
              <div className='logos'>
                <a
                  href='https://panteracapital.com'
                  target='blank'
                  className='pantera'
                >
                  <div className='logo'></div>
                  <span>Pantera Capital</span>
                </a>
                <a
                  href='https://www.cluster.vc'
                  target='blank'
                  className='cluster'
                >
                  <div className='logo'></div>
                  <span>Cluster Capital</span>
                </a>
                <a
                  href='https://lemniscap.com'
                  target='blank'
                  className='lemniscap'
                >
                  <div className='logo'></div>
                  <span>LemnisCap</span>
                </a>
                <a
                  href='https://www.deepventures.io'
                  target='blank'
                  className='deepventures'
                >
                  <div className='logo'></div>
                  <span>Deep Ventures</span>
                </a>
                <a
                  href='https://www.angeldao.org'
                  target='blank'
                  className='angeldao'
                >
                  <div className='logo'></div>
                  <span>AngelDAO</span>
                </a>
                <a
                  href='https://http://www.kretos.ventures/'
                  target='blank'
                  className='kretos'
                >
                  <div className='logo'></div>
                  <span>Kretos Ventures</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <footer>
          <div id='landing-details'>
            <div id='priv-terms'>
              <a
                href='https://docs.yolorekt.finance/docs/legal/privacy-policy'
                target='blank'
              >
                Privacy Policy
              </a>
              &nbsp;&nbsp;•&nbsp;&nbsp;
              <a
                href='https://docs.yolorekt.finance/docs/legal/terms-of-service'
                target='blank'
              >
                Terms of Service
              </a>
              &nbsp;&nbsp;•&nbsp;&nbsp;
              <a href='https://docs.yolorekt.finance' target='blank'>
                Docs
              </a>
            </div>

            <div id='copyright'>
              <strong>
                © Copyright{' '}
                <script type='text/javascript'>
                  var year = new Date();document.write(year.getFullYear());
                </script>{' '}
                2023
              </strong>
              All rights reserved
            </div>
          </div>

          <div className='about'>
            YOLOREKT designs, builds, and provides advanced next-degen leveraged
            gaming experiences that offer killer incentives, royalties, and
            leverage up-to 1000x with no minimum portfolio balance.
            <div>
              Visit and play YOLOREKT @{' '}
              <a href='https://yolorekt.finance' target='blank'>
                https://yolorekt.finance
              </a>
            </div>
          </div>

          <div id='landing-social'>
            <a
              href='https://twitter.com/playyolorekt'
              target='blank'
              rel='noopener noreferrer'
              className='icon twitter'
            >
              {''}
            </a>
            <a
              href='https://vm.tiktok.com/ZTdmEw6xk/'
              target='blank'
              rel='noopener noreferrer'
              className='icon tiktok'
            >
              {''}
            </a>
            <a
              href='https://medium.com/yolorekt'
              target='blank'
              rel='noopener noreferrer'
              className='icon medium'
            >
              {''}
            </a>
            <a
              href='https://t.me/joinchat/NBBv1hQmV26flFM_5MD4_w'
              target='blank'
              rel='noopener noreferrer'
              className='icon telegram'
            >
              {''}
            </a>
            <a
              href='https://discord.gg/yolorekt'
              target='blank'
              rel='noopener noreferrer'
              className='icon discord'
            >
              {''}
            </a>
            <a
              href='mailto:support@yolorekt.com?Subject=Hello%20again'
              target='blank'
              rel='noopener noreferrer'
              className='icon email'
            >
              {''}
            </a>
          </div>
        </footer>
      </div>
    </PublicPresaleCSS>
  )
}
